import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CssBaseline } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import ErrorBoundary from './components/ErrorBoundary';
import { SecurityProvider } from './components/SecurityHeaders';
import Layout from './components/Layout';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Pages
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import Profile from './pages/Profile';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AcceptInvitation from './pages/AcceptInvitation';
import Unauthorized from './pages/Unauthorized';
import Users from './pages/Users';
import ManagerAssignment from './pages/ManagerAssignment';
import Trainings from './pages/Trainings';
import TrainingContent from './pages/TrainingContent';

import MyAssignments from './pages/MyAssignments';
import Checklists from './pages/Checklists';
import ChecklistCompletions from './pages/ChecklistCompletions';
import ChecklistCompletionDetail from './pages/ChecklistCompletionDetail';
import Logs from './pages/Logs';
import LogEntries from './pages/LogEntries';
import Documents from './pages/Documents';
import DocumentTemporaryRestore from './pages/DocumentTemporaryRestore';
import Assignments from './pages/Assignments';
import NotificationSettings from './pages/NotificationSettings';
import DocumentReview from './pages/DocumentReview';
import ChecklistPlayer from './components/checklists/ChecklistPlayer';
import LogPlayer from './components/logs/LogPlayer';
import SecurityTestRunner from './components/SecurityTestRunner';
import CompanySettings from './pages/CompanySettings';
import ApiKeys from './pages/ApiKeys';
import SuperuserActions from './pages/SuperuserActions';
import AuditDashboard from './pages/AuditDashboard';

function App() {
  return (
    <ErrorBoundary>
      <SecurityProvider>
        <AuthProvider>
          <NotificationProvider>
            <CssBaseline />
            <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/accept-invitation/:token" element={<AcceptInvitation />} />
        <Route path="/accept-invitation" element={<AcceptInvitation />} />
        <Route path="/unauthorized" element={<Unauthorized />} />

        {/* Protected routes */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Box sx={{ display: 'flex' }}>
                <Layout>
                  <Routes>
                    {/* Dashboard - accessible to all authenticated users */}
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/profile" element={<Profile />} />

                    {/* Employee routes */}
                    <Route path="/my-assignments" element={<MyAssignments />} />
                    <Route path="/notification-settings" element={<NotificationSettings />} />
      
                    <Route path="/checklist-player/:assignmentId" element={<ChecklistPlayer />} />
                    <Route path="/log-player/:assignmentId" element={<LogPlayer />} />
                    <Route path="/document-review/:documentId" element={<DocumentReview />} />

                    {/* Manager and Admin routes */}
                    <Route
                      path="/assignments"
                      element={
                        <ProtectedRoute allowRoles={['admin', 'manager']}>
                          <Assignments />
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin only routes */}
                    <Route
                      path="/users"
                      element={
                        <ProtectedRoute requireRole="admin">
                          <Users />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/manager-assignment"
                      element={
                        <ProtectedRoute requireRole="admin">
                          <ManagerAssignment />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/trainings"
                      element={
                        <ProtectedRoute requireRole="admin">
                          <Trainings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/training-content"
                      element={
                        <ProtectedRoute requireRole="admin">
                          <TrainingContent />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/company-settings"
                      element={
                        <ProtectedRoute requireRole="admin">
                          <CompanySettings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/api-keys"
                      element={
                        <ProtectedRoute requireRole="admin">
                          <ApiKeys />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/audit-dashboard"
                      element={
                        <ProtectedRoute requireRole="admin">
                          <AuditDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/superuser-actions"
                      element={
                        <ProtectedRoute requireSuperuser={true}>
                          <SuperuserActions />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/checklists"
                      element={
                        <ProtectedRoute requireRole="admin">
                          <Checklists />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/checklists/completions"
                      element={
                        <ProtectedRoute requireRole="admin">
                          <ChecklistCompletions />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/checklists/completions/:completionId"
                      element={
                        <ProtectedRoute requireRole="admin">
                          <ChecklistCompletionDetail />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/logs"
                      element={
                        <ProtectedRoute requireRole="admin">
                          <Logs />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/logs/entries"
                      element={
                        <ProtectedRoute allowRoles={['admin', 'manager']}>
                          <LogEntries />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/documents/temporary"
                      element={
                        <ProtectedRoute requireRole="admin">
                          <DocumentTemporaryRestore />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/documents"
                      element={
                        <ProtectedRoute requireRole="admin">
                          <Documents />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/security-tests"
                      element={
                        <ProtectedRoute requireRole="admin">
                          <SecurityTestRunner />
                        </ProtectedRoute>
                      }
                    />

                    {/* Catch all - redirect to dashboard */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Layout>
              </Box>
            </ProtectedRoute>
          }
        />
            </Routes>
          </NotificationProvider>
        </AuthProvider>
      </SecurityProvider>
    </ErrorBoundary>
  );
}

export default App; 